import { z } from 'zod';
import { ValidateIdNumber } from '../components/Validation/IdNumberValidator';
import {
  emailContraints,
  incomeConstraints,
  mobileNumberConstraints,
  newEmailContraints,
  otpContraints,
  passwordConstraints,
} from './CommonContraints';
import {
  BankAccountType,
  EmploymentIndustryEnum,
  EmploymentStatusEnum,
  HomeStatusEnum,
  IncomeFrequencyEnum,
  PositionEnum,
} from '../clients/AccountClient';
import RegularExpressions from '../components/Validation/RegularExpressions';
import { BeneficiaryRelationshipEnum } from '../models/enums/BeneficiaryRelationshipEnum';
import { AffordabilityChallengeReasons } from '../utils/Helpers/AffordabilityChallengeHelper';

export const signInFormSchema = z.object({
  email: z.string().email('Please enter a valid email address.'),
  password: z.string().nonempty({ message: 'This field is required' }),
});

export const loginFormSchema = z
  .object({
    email: z.string(),
    password: z.string().nonempty({ message: 'This field is required' }),
  })
  .superRefine((data, ctx) => {
    const email = data.email;
    const emailPassed = new RegExp(RegularExpressions.email).test(email);

    if (!emailPassed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid email address.',
        path: ['email'],
      });
    }

    return true;
  });


export const registerFormPartialSchema = z.object({
  firstname: z
    .string()
    .trim()
    .min(2, { message: "Please enter a valid first name" })
    .max(16, { message: "Must be 16 characters or less" })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: "You have entered illegal characters in this field",
    })
    .regex(new RegExp(RegularExpressions.noNumeric), {
      message: "No numbers allowed",
    }),
  lastname: z
    .string()
    .trim()
    .min(2, { message: "Please enter a valid last name" })
    .max(16, { message: "Must be 16 characters or less" })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: "You have entered illegal characters in this field",
    })
    .regex(new RegExp(RegularExpressions.noNumeric), {
      message: "No numbers allowed",
    }),
  idNumber: z
    .string()
    .min(13, { message: "This is not a valid RSA ID" })
    .max(13, { message: "This is not a valid RSA ID" })
    .superRefine((id, ctx) => {
      const errorMessage = ValidateIdNumber(id);
      if (errorMessage !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${errorMessage}`,
        });
      }
    }),
});

// Full schema (partial1 + partial2)
export const registerFormSchemaV2 = (invalidCellphoneNumber: string) =>
  registerFormPartialSchema
    .merge(
      z.object({
        email: newEmailContraints,
        cellphoneNumber: mobileNumberConstraints,
        password: passwordConstraints,
        marketing: z.boolean(),
      })
    )
    .superRefine((data, ctx) => {
      if (invalidCellphoneNumber === data.cellphoneNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter a valid 10 digit number starting with 0",
          path: ["cellphoneNumber"],
        });
      }
    });

export const registerFormSchema = (lastEmailExists: string) =>
  z
    .object({
      firstname: z
        .string()
        .trim()
        .min(2, { message: 'Please enter a valid first name' })
        .max(75, { message: 'Please enter a valid first name' })
        .regex(new RegExp(RegularExpressions.noSpecialChars), {
          message: 'You have entered illegal characters in this field',
        })
        .regex(new RegExp(RegularExpressions.noNumeric), {
          message: 'No numbers allowed',
        }),
      lastname: z
        .string()
        .trim()
        .min(2, { message: 'Please enter a valid last name' })
        .max(75, { message: 'Please enter a valid last name' })
        .regex(new RegExp(RegularExpressions.noSpecialChars), {
          message: 'You have entered illegal characters in this field',
        })
        .regex(new RegExp(RegularExpressions.noNumeric), {
          message: 'No numbers allowed',
        }),
      idNumber: z
        .string()
        .min(13, { message: 'This is not a valid RSA ID' })
        .max(13, { message: 'This is not a valid RSA ID' })
        .superRefine((id, ctx) => {
          const errorMessage = ValidateIdNumber(id);
          if (errorMessage !== undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${errorMessage}`,
            });
          }
        }),
      email: newEmailContraints,
      password: passwordConstraints,
      privacyPolicyAccepted: z.boolean({
        required_error: 'Please accept the privacy policy',
      }),
    })
    .refine((data) => lastEmailExists !== data.email, {
      message: 'This email is already in use, please log in',
      path: ['email'],
    });

export const passwordSchema = z
  .object({
    password: passwordConstraints,
    newPassword: passwordConstraints,
    confirmNewPassword: passwordConstraints,
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords don't match",
    path: ['confirmNewPassword'], // path of error
  })
  .refine((data) => data.password !== data.newPassword, {
    message: 'New password cannot equal to the current password',
    path: ['newPassword'], // path of error
  });

export const addressSchema = z.object({
  houseNumber: z
    .string()
    .trim()
    .min(1, { message: 'Not a valid house number' })
    .max(50, { message: 'Consider using a short house number' })
    .regex(new RegExp(RegularExpressions.alphaNumeric), {
      message: 'Field must contains alphanumeric values',
    }),
  streetName: z
    .string()
    .trim()
    .min(2, { message: 'Not a valid Street name' })
    .max(50, { message: 'Consider using a short Street name' })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: 'Field must contains no special characters',
    }),
  suburb: z
    .string()
    .trim()
    .min(2, { message: 'Not a valid Suburb' })
    .max(50, { message: 'Consider using a short Suburb name' })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: 'Field must contains no special characters',
    }),
  city: z
    .string()
    .trim()
    .min(2, { message: 'Not a valid City' })
    .max(50, { message: 'Consider using a short City name' })
    .regex(new RegExp(RegularExpressions.textOnly), {
      message: 'Field must contains only letters',
    }),
  province: z
    .string()
    .min(2, { message: 'Not a valid Province' })
    .max(50, { message: 'Consider using a short Province name' }),
  postalCode: z
    .string()
    .trim()
    .min(4, { message: 'This is not a valid postcode' })
    .max(4, { message: 'This is not a valid postcode' })
    .regex(new RegExp(RegularExpressions.numericOnly), {
      message: 'Field must contains only numbers',
    }),
});

export const cellphoneSchema = z.object({
  cellphoneNumber: mobileNumberConstraints,
});

export const OTPSchema = z.object({
  cellphoneNumber: mobileNumberConstraints,
  otp: otpContraints,
});

export const emailSchema = z.object({
  email: emailContraints,
});

export const changeEmailSchema = z.object({
  email: newEmailContraints,
});

export const nameSchema = z.object({
  name: z.string(),
});

export const marketingPreferenceSchema = z.object({
  marketing: z.boolean(),
});

export enum MaritalStatus {
  Unspecified = "Unspecified",
  Married = 'Married',
  Unmarried = 'Unmarried'
}

export const customerDetailsSchema = z.object({
  homeStatus: z.nativeEnum(HomeStatusEnum).refine((value) => value !== HomeStatusEnum.Unspecified, {
    message: 'This field is required',
  }),
  maritalStatus: z.nativeEnum(MaritalStatus).refine((value) => value !== MaritalStatus.Unspecified, {
    message: 'This field is required',
  }),
  marriedInCommunityOfProperty: z.boolean(),
  houseNumber: z
    .string()
    .trim()
    .min(1, { message: 'Not a valid house number' })
    .max(50, { message: 'Consider using a short house number' })
    .regex(new RegExp(RegularExpressions.alphaNumeric), {
      message: 'Field must contains alphanumeric values',
    }),
  streetName: z
    .string()
    .trim()
    .min(2, { message: 'Not a valid Street name' })
    .max(50, { message: 'Consider using a short Street name' })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: 'Field must contains no special characters',
    }),
  suburb: z
    .string()
    .trim()
    .min(2, { message: 'Not a valid Suburb' })
    .max(50, { message: 'Consider using a short Suburb name' })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: 'Field must contains no special characters',
    }),
  city: z
    .string()
    .trim()
    .min(2, { message: 'Not a valid City' })
    .max(50, { message: 'Consider using a short City name' })
    .regex(new RegExp(RegularExpressions.textOnly), {
      message: 'Field must contains only letters',
    }),
  province: z
    .string()
    .min(2, { message: 'Not a valid Province' })
    .max(50, { message: 'Consider using a short Province name' }),
  postalCode: z
    .string()
    .trim()
    .min(4, { message: 'This is not a valid postcode' })
    .max(4, { message: 'This is not a valid postcode' })
    .regex(new RegExp(RegularExpressions.numericOnly), {
      message: 'Field must contains only numbers',
    }),
});

export const employmentDetailsSchema = z
  .object({
    employerName: z.string().trim().max(100, { message: 'Employer name must be less than 100 characters' }).optional(),
    position: z.nativeEnum(PositionEnum),
    industry: z.nativeEnum(EmploymentIndustryEnum),
    netIncome: incomeConstraints,
    incomeFrequency: z.nativeEnum(IncomeFrequencyEnum),
    salaryDay: z.string().superRefine((value, ctx) => {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'This field is required',
        });
      }
      if (numValue < 1 || numValue > 31) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Only numbers between 1 and 31 to be used',
        });
      }
    }),
    employmentStatus: z.nativeEnum(EmploymentStatusEnum),
    employedOn: z.date().optional(),
    grossIncome: incomeConstraints,
  })
  .refine((data) => parseInt(data.netIncome) <= parseInt(data.grossIncome), {
    message: 'Net income needs to be less than Gross Income',
    path: ['netIncome'],
  })
  .refine(
    (data) => {
      const isFullTimeEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedFullTime;
      const isPartTimeEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedPartTime;
      const isTemporaryEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedTemporary;
      if (isFullTimeEmployed || isPartTimeEmployed || isTemporaryEmployed) {
        return data.employerName != null && data.employerName.length > 1;
      }
      return true;
    },
    {
      message: 'This field is required',
      path: ['employerName'],
    }
  )
  .refine(
    (data) => {
      if (data.incomeFrequency !== IncomeFrequencyEnum.Unspecified)
        return true;
    },
    {
      message: 'This field is required',
      path: ['incomeFrequency'],
    }
  )
  .refine(
    (data) => {
      const isFullTimeEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedFullTime;
      const isPartTimeEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedPartTime;
      const isTemporaryEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedTemporary;
      const isValidEmployerName = new RegExp(RegularExpressions.onlyStandardCharacters).test(data.employerName ?? '');
      if (isFullTimeEmployed || isPartTimeEmployed || isTemporaryEmployed) {
        return isValidEmployerName && data.employerName != null && data.employerName.length > 1;
      }
      return true;
    },
    {
      message: 'Enter a valid employer name',
      path: ['employerName'],
    }
  )
  .refine(
    (data) => {
      const isFullTimeEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedFullTime;
      const isPartTimeEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedPartTime;
      const isTemporaryEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedTemporary;
      if (isFullTimeEmployed || isPartTimeEmployed || isTemporaryEmployed) {
        return data.industry != EmploymentIndustryEnum.Unspecified;
      }
      return true;
    },
    {
      message: 'This field is required',
      path: ['industry'],
    }
  )
  .refine(
    (data) => {
      const isFullTimeEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedFullTime;
      const isPartTimeEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedPartTime;
      const isTemporaryEmployed = data.employmentStatus === EmploymentStatusEnum.EmployedTemporary;
      if (isFullTimeEmployed || isPartTimeEmployed || isTemporaryEmployed) {
        return data.position != PositionEnum.Unspecified;
      }
      return true;
    },
    {
      message: 'This field is required',
      path: ['position'],
    }
  )
  .superRefine((data, ctx) => {
    if (data.employmentStatus === EmploymentStatusEnum.Unspecified) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["employmentStatus"],
        message: "This field is required",
      });
    }
  });

export const newCustomerEmploymentDetailsSchemaBuilder = (restrictedSalaryDays: number[]) => {
  const overwrites = z.object({
    salaryDay: z.string().superRefine((value, ctx) => {
      const numValue = parseInt(value, 10);
      if (restrictedSalaryDays.includes(numValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Repayment date unavailable',
        });
      }
    }),
  });

  return z.intersection(employmentDetailsSchema, overwrites);
};

export const affordabilitySchema = z
  .object({
    grossIncome: z.string(),
    netIncome: z.string(),
    goods: z.string().regex(new RegExp(RegularExpressions.numericOnly), {
      message: 'This value is too small',
    }),
    loanRepayments: z.string().regex(new RegExp(RegularExpressions.numericOnly), {
      message: 'This value is too small',
    }),
    disposableMonthlyIncome: z.string(),
  })
  .refine((data) => parseInt(data.disposableMonthlyIncome) > 0, {
    message: 'Calculated disposable income must be greater than 0',
    path: ['disposableMonthlyIncome'], // path of error
  });

export const bankSchema = z
  .object({
    accountNumber: z.string().regex(new RegExp(RegularExpressions.numericOnly), {
      message: 'The number must be positive',
    }),
    accountType: z.nativeEnum(BankAccountType),
    holderName: z.string(),
    bankName: z.string().nonempty('This field is required'),
    bankCode: z.string(),
    openedOn: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!data.bankName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['bankName'],
      });

      return;
    }

    if (data.accountType === BankAccountType.Unspecified) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['accountType'],
      });

      return;
    }

    const bankName = data.bankName;
    const accountNumber = data.accountNumber;
    const savings = BankAccountType.Savings;
    const current = BankAccountType.Current;
    const transmission = BankAccountType.Transmission;

    if (bankName === 'ABSA') {
      const accountRules = [
        { type: savings, min: 1000000, max: 99999999999, minCharacterLength: 7, maxCharacterLength: 11 },
        { type: current, min: 1000000, max: 99999999999, minCharacterLength: 7, maxCharacterLength: 11 },
        { type: transmission, min: 1000000, max: 99999999999, minCharacterLength: 7, maxCharacterLength: 11 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for ABSA bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;
      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Absa bank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'Capitec') {
      const accountRules = [
        { type: savings, min: 1000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
        { type: current, min: 1000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
        { type: transmission, min: 1000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for Capitec bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;
      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Capitec bank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'First National Bank') {
      const accountRules = [
        { type: savings, min: 50000000000, max: 69999999999, minCharacterLength: 11, maxCharacterLength: 11 },
        { type: current, min: 50000000000, max: 69999999999, minCharacterLength: 11, maxCharacterLength: 11 },
        { type: transmission, min: 50000000000, max: 69999999999, minCharacterLength: 11, maxCharacterLength: 11 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for First National Bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;
      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid First National Bank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'Nedbank') {
      const accountRules = [
        { type: savings, min: 2000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
        { type: current, min: 1000000000, max: 1999999999, minCharacterLength: 10, maxCharacterLength: 10 },
        { type: transmission, min: 2000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for Nedbank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;
      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Nedbank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'Standard Bank') {
      const accountRules = [
        { type: savings, min: 0, max: 19999999999, minCharacterLength: 9, maxCharacterLength: 11 },
        { type: current, min: 0, max: 19999999999, minCharacterLength: 9, maxCharacterLength: 11 },
        { type: transmission, min: 0, max: 19999999999, minCharacterLength: 9, maxCharacterLength: 11 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for Standard Bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;

      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Standard Bank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'Discovery Bank') {
      const accountRules = [
        { type: savings, min: 1, max: 99999999999, minCharacterLength: 11, maxCharacterLength: 11 },
        { type: current, min: 1, max: 99999999999, minCharacterLength: 11, maxCharacterLength: 11 },
        { type: transmission, min: 1, max: 99999999999, minCharacterLength: 11, maxCharacterLength: 11 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for Discovery Bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;

      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Discovery Bank account number',
          path: ['accountNumber'],
        });
      }

      //Discovery does not currently support transmission accounts
      if (data.accountType === transmission) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Discovery Bank account type',
          path: ['accountType'],
        });
      }
    }

    if (bankName === 'TymeBank') {
      if (accountNumber.length !== 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid TymeBank account number',
          path: ['accountNumber'],
        });
      }
    }

    return true;
  });

export const limpBankSchema = z
  .object({
    accountNumber: z.string().regex(new RegExp(RegularExpressions.numericOnly), {
      message: 'The number must be positive',
    }),
    salaryDay: z.string().refine((val) => {
      const num = Number(val);
      return !isNaN(num) && num >= 1 && num <= 31;
    }, { message: 'Invalid salary day' }),
    accountType: z.nativeEnum(BankAccountType),
    holderName: z.string(),
    bankName: z.string().nonempty('This field is required'),
    bankCode: z.string(),
    openedOn: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!data.bankName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['bankName'],
      });

      return;
    }

    if (data.accountType === BankAccountType.Unspecified) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: ['accountType'],
      });

      return;
    }

    const bankName = data.bankName;
    const accountNumber = data.accountNumber;
    const salaryDay = data.salaryDay;
    const savings = BankAccountType.Savings;
    const current = BankAccountType.Current;
    const transmission = BankAccountType.Transmission;

    if (bankName === 'ABSA') {
      const accountRules = [
        { type: savings, min: 1000000, max: 99999999999, minCharacterLength: 7, maxCharacterLength: 11 },
        { type: current, min: 1000000, max: 99999999999, minCharacterLength: 7, maxCharacterLength: 11 },
        { type: transmission, min: 1000000, max: 99999999999, minCharacterLength: 7, maxCharacterLength: 11 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for ABSA bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;
      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Absa bank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'Capitec') {
      const accountRules = [
        { type: savings, min: 1000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
        { type: current, min: 1000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
        { type: transmission, min: 1000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for Capitec bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;
      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Capitec bank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'First National Bank') {
      const accountRules = [
        { type: savings, min: 50000000000, max: 69999999999, minCharacterLength: 11, maxCharacterLength: 11 },
        { type: current, min: 50000000000, max: 69999999999, minCharacterLength: 11, maxCharacterLength: 11 },
        { type: transmission, min: 50000000000, max: 69999999999, minCharacterLength: 11, maxCharacterLength: 11 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for First National Bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;
      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid First National Bank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'Nedbank') {
      const accountRules = [
        { type: savings, min: 2000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
        { type: current, min: 1000000000, max: 1999999999, minCharacterLength: 10, maxCharacterLength: 10 },
        { type: transmission, min: 2000000000, max: 2999999999, minCharacterLength: 10, maxCharacterLength: 10 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for Nedbank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;
      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Nedbank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'Standard Bank') {
      const accountRules = [
        { type: savings, min: 0, max: 19999999999, minCharacterLength: 9, maxCharacterLength: 11 },
        { type: current, min: 0, max: 19999999999, minCharacterLength: 9, maxCharacterLength: 11 },
        { type: transmission, min: 0, max: 19999999999, minCharacterLength: 9, maxCharacterLength: 11 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for Standard Bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;

      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Standard Bank account number',
          path: ['accountNumber'],
        });
      }
    }

    if (bankName === 'Discovery Bank') {
      const accountRules = [
        { type: savings, min: 1, max: 99999999999, minCharacterLength: 11, maxCharacterLength: 11 },
        { type: current, min: 1, max: 99999999999, minCharacterLength: 11, maxCharacterLength: 11 },
        { type: transmission, min: 1, max: 99999999999, minCharacterLength: 11, maxCharacterLength: 11 },
      ];

      const rule = accountRules.find((accRule) => accRule.type === data.accountType);

      if (!rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid account type for Discovery Bank',
          path: ['accountType'],
        });

        return;
      }

      const { min, max, minCharacterLength, maxCharacterLength } = rule;

      const accountNumberInt = parseInt(accountNumber);

      if (isNaN(accountNumberInt) || accountNumberInt < min || accountNumberInt > max || accountNumber.length < minCharacterLength || accountNumber.length > maxCharacterLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Discovery Bank account number',
          path: ['accountNumber'],
        });
      }

      //Discovery does not currently support transmission accounts
      if (data.accountType === transmission) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Discovery Bank account type',
          path: ['accountType'],
        });
      }
    }

    if (bankName === 'TymeBank') {
      if (accountNumber.length !== 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid TymeBank account number',
          path: ['accountNumber'],
        });
      }
    }

    return true;
  });

export const termsAndConditionsSchema = z.object({
  loanAgreementAccepted: z.boolean(),
  // debitOrderAuthorisation: z.boolean(),
});

export const confirmPasswordResetSchema = z.object({
  email: z.string().email('Please enter a valid email address.'),
  newPassword: z.string(),
  confirmNewPassword: z.string().nonempty('This field is required'),
});

export const repaySchema = (currentBalance: number, minimumAmountInRands: number) =>
  z
    .object({
      repayAmount: z.string(),
    })
    .refine((data) => parseFloat(data.repayAmount) >= minimumAmountInRands, {
      message: `Must be R${minimumAmountInRands} or more`,
      path: ['repayAmount'],
    })
    .refine((data) => parseFloat(data.repayAmount) <= currentBalance, {
      message: `Must be R${currentBalance} or less`,
      path: ['repayAmount'],
    });

export const changeMobileNumberSchema = (previousMobileNumber: string) =>
  z
    .object({
      cellphoneNumber: mobileNumberConstraints,
    })
    .refine((data) => data.cellphoneNumber !== previousMobileNumber, {
      message: `This number already exists`,
      path: ['cellphoneNumber'],
    });

export const limiterFormSchema = z.object({
  firstname: z
    .string()
    .trim()
    .min(2, { message: 'Please enter a valid first name' })
    .max(75, { message: 'Please enter a valid first name' })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: 'You have entered illegal characters in this field',
    })
    .regex(new RegExp(RegularExpressions.noNumeric), {
      message: 'No numbers allowed',
    }),
  lastname: z
    .string()
    .trim()
    .min(2, { message: 'Please enter a valid last name' })
    .max(75, { message: 'Please enter a valid last name' })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: 'You have entered illegal characters in this field',
    })
    .regex(new RegExp(RegularExpressions.noNumeric), {
      message: 'No numbers allowed',
    }),
  email: newEmailContraints,
  cellphoneNumber: mobileNumberConstraints,
});

export const creditLifePlusBeneficiarySchema = z.object({
  firstname: z
    .string()
    .trim()
    .min(2, { message: 'Please enter a valid first name' })
    .max(75, { message: 'Please enter a valid first name' })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: 'You have entered illegal characters in this field',
    })
    .regex(new RegExp(RegularExpressions.noNumeric), {
      message: 'No numbers allowed',
    }),
  lastname: z
    .string()
    .trim()
    .min(2, { message: 'Please enter a valid last name' })
    .max(75, { message: 'Please enter a valid last name' })
    .regex(new RegExp(RegularExpressions.noSpecialChars), {
      message: 'You have entered illegal characters in this field',
    })
    .regex(new RegExp(RegularExpressions.noNumeric), {
      message: 'No numbers allowed',
    }),
  beneficiaryRelationship: z
    .nativeEnum(BeneficiaryRelationshipEnum)
    .refine((value) => value !== BeneficiaryRelationshipEnum.Unspecified, {
      message: 'This field is required',
    }),
  cellphoneNumber: mobileNumberConstraints,
});

export const confirmIdNumberFormSchema = z.object({
  idNumber: z
    .string()
    .min(13, { message: 'This is not a valid RSA ID' })
    .max(13, { message: 'This is not a valid RSA ID' })
    .superRefine((id, ctx) => {
      const errorMessage = ValidateIdNumber(id);
      if (errorMessage !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${errorMessage}`,
        });
      }
    }),
});

export const confirmOTPFormSchema = z.object({
  otp: otpContraints,
});

export const updateIncomeSchema = z
  .object({
    netIncome: incomeConstraints,
    grossIncome: incomeConstraints,
  })
  .refine((data) => parseInt(data.netIncome) <= parseInt(data.grossIncome), {
    message: 'Net income needs to be less than Gross Income',
    path: ['netIncome'],
  });

export const incomeAndExpensesSchema = z
  .object({
    netIncome: incomeConstraints,
    grossIncome: incomeConstraints,
    goods: z.string().regex(new RegExp(RegularExpressions.numericOnly), {
      message: 'This value is too small',
    }),
    loanRepayments: z.string().regex(new RegExp(RegularExpressions.numericOnly), {
      message: 'This value is too small',
    }),
    disposableMonthlyIncome: z.string(),
  })
  .refine((data) => parseInt(data.netIncome) <= parseInt(data.grossIncome), {
    message: 'Net income needs to be less than Gross Income',
    path: ['netIncome'],
  })
  .refine((data) => parseInt(data.disposableMonthlyIncome) > 0, {
    message: 'Calculated disposable income must be greater than 0',
    path: ['disposableMonthlyIncome'], // path of error
  });


export const affordabilityChallengeSchema = (minimumAmount: number, maximumAmount: number) =>
  z
    .object({
      overrideAmount: z
        .string(),
      challengeReason: z
        .string()
    })
    .superRefine((data, ctx) => {
      if (!data.challengeReason) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["challengeReason"],
          message: "Please select a reason",
        });
      } else {
        if (AffordabilityChallengeReasons[data.challengeReason].overrideAmountRequired) {
          if (
            data.overrideAmount.trim() === "" ||
            Number.isNaN(Number(data.overrideAmount)) ||
            +data.overrideAmount <= 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["overrideAmount"],
              message: "Please enter a valid amount",
            });
          } else if (+data.overrideAmount < minimumAmount) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["overrideAmount"],
              message: `Must be R${minimumAmount} or more`,
            });
          } else if (+data.overrideAmount >= maximumAmount) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["overrideAmount"],
              message: `Must be less than R${maximumAmount}`,
            });
          }
        }
      }

    });

export const affordabilityChallengeConfirmSchema = z.object({
  isConfirmed: z.literal(true)
})